// src/pages/checkin/CheckinHome.jsx
// Ponto de entrada público para o check-in. Nenhum login necessário.
// O operador digita o código gerado no dashboard para acessar o scanner.
import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import { get, ref } from "firebase/database";
import { rtdb } from "../../services/firebase/rtdb";
import { useNavigate } from "react-router-dom";

const bgSx = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  px: 2,
  background:
    "radial-gradient(1200px circle at 20% 10%, rgba(34,197,94,0.14), transparent 55%), " +
    "radial-gradient(900px circle at 80% 30%, rgba(59,130,246,0.10), transparent 55%), #070b14",
};

const glassSx = {
  p: { xs: 3, sm: 4 },
  borderRadius: "24px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.10)",
  backdropFilter: "blur(20px)",
  width: "100%",
  maxWidth: 440,
};

export default function CheckinHome() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleAccess() {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;

    setLoading(true);
    setError("");

    try {
      const snap = await get(ref(rtdb, `checkinSessions/${trimmed}`));

      if (!snap.exists()) {
        setError("Código não encontrado. Verifique e tente novamente.");
        return;
      }

      const session = snap.val();

      if (!session.active) {
        setError("Este código foi desativado pelo organizador.");
        return;
      }

      navigate(`/checkin/scanner/${trimmed}`);
    } catch (err) {
      console.error(err);
      setError("Erro ao verificar o código. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={bgSx}>
      <Paper sx={glassSx} elevation={0}>
        <Stack spacing={3}>
          {/* Header */}
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, rgba(0,180,255,0.85), rgba(0,255,180,0.75))",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <QrCodeScannerRoundedIcon sx={{ fontSize: 20, color: "#fff" }} />
              </Box>
              <Typography sx={{ fontWeight: 900, fontSize: 20 }}>
                ONURB PASS · Check-in
              </Typography>
            </Stack>
            <Typography sx={{ opacity: 0.65, fontSize: 13, pl: "48px" }}>
              Acesso de operador de portaria
            </Typography>
          </Stack>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

          <Stack spacing={1.2}>
            <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
              Código de acesso
            </Typography>
            <Typography sx={{ opacity: 0.6, fontSize: 13 }}>
              Digite o código fornecido pelo organizador do evento.
            </Typography>

            <TextField
              value={code}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && handleAccess()}
              placeholder="Ex: AB12C3"
              fullWidth
              inputProps={{
                style: {
                  fontFamily: "monospace",
                  fontSize: 22,
                  fontWeight: 900,
                  letterSpacing: "0.18em",
                  textAlign: "center",
                  textTransform: "uppercase",
                },
                maxLength: 6,
              }}
              error={!!error}
              helperText={error}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  background: "rgba(0,0,0,0.28)",
                  "& fieldset": { borderColor: error ? "#f87171" : "rgba(255,255,255,0.14)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.28)" },
                  "&.Mui-focused fieldset": { borderColor: "rgba(34,197,94,0.7)" },
                },
                "& .MuiFormHelperText-root": { color: "#f87171", fontWeight: 700 },
              }}
            />
          </Stack>

          <Button
            fullWidth
            size="large"
            onClick={handleAccess}
            disabled={loading || code.trim().length === 0}
            sx={{
              py: 1.4,
              borderRadius: "14px",
              fontWeight: 900,
              fontSize: 15,
              background: "linear-gradient(-135deg, #22c55e, #4ade80)",
              color: "#052e16",
              "&:disabled": { background: "rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.3)" },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: "#052e16" }} /> : "Acessar scanner"}
          </Button>

          <Typography sx={{ textAlign: "center", fontSize: 12, opacity: 0.4 }}>
            O código é gerado pelo organizador no painel de controle.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
